import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DateRangePicker } from '../components/DateRangePicker';
import { TrendingUp, TrendingDown, Minus, Maximize2, Plug, RefreshCw, AlertTriangle, Download, FileText, Eye, EyeOff } from 'lucide-react';
import { SalesProjectionView, MASKED_BRL } from '../components/SalesProjectionView';
import { buildPrivateLabels } from '../lib/privateLabels';
import { KpiCards } from '../components/dashboard/KpiCards';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const num = (v) => new Intl.NumberFormat('pt-BR').format(v || 0);

const PERIOD_OPTIONS = [
  { value: 'hoje', label: 'Hoje' },
  { value: 'ontem', label: 'Ontem' },
  { value: 'ultimos_7_dias', label: 'Últimos 7 dias' },
  { value: 'ultimos_30_dias', label: 'Últimos 30 dias' },
  { value: 'ultimos_6_meses', label: 'Últimos 6 meses' },
  { value: 'semana_atual', label: 'Semana atual' },
  { value: 'mes_atual', label: 'Mês atual' },
  { value: 'personalizado', label: 'Personalizado' },
];

const PERIODS_WITH_TODAY = new Set([
  'hoje', 'ultimos_7_dias', 'ultimos_30_dias', 'ultimos_6_meses', 'semana_atual', 'mes_atual',
]);

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayISO = () => fmtDate(new Date());

const etaLabel = (startedAt, progress) => {
  if (!startedAt || !progress || progress <= 0 || progress >= 100) return null;
  const elapsed = (Date.now() - new Date(startedAt).getTime()) / 1000;
  if (elapsed < 5) return null;
  const remaining = Math.max((elapsed / (progress / 100)) - elapsed, 0);
  if (remaining < 60) return '≈ menos de 1 min restante';
  const mins = Math.round(remaining / 60);
  if (mins < 60) return `≈ ${mins} min restantes`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `≈ ${h}h${m > 0 ? ' ' + m + 'min' : ''} restantes`;
};

const Delta = ({ pct, size = 'sm' }) => {
  if (pct === null || pct === undefined) {
    return <span className="text-muted-foreground font-mono">—</span>;
  }
  const up = pct > 0, down = pct < 0;
  const color = up ? 'text-green-500' : down ? 'text-red-500' : 'text-muted-foreground';
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 font-mono ${color} ${size === 'lg' ? 'text-base' : 'text-xs'}`} data-testid="delta">
      <Icon className={size === 'lg' ? 'h-4 w-4' : 'h-3 w-3'} />
      {Math.abs(pct).toFixed(1).replace('.', ',')}%
    </span>
  );
};

const deltaCellStyle = (pct) => {
  if (pct === null || pct === undefined) return {};
  const alpha = Math.min(Math.abs(pct) / 100, 1) * 0.32 + 0.05;
  const rgb = pct >= 0 ? '34,197,94' : '239,68,68';
  return { backgroundColor: `rgba(${rgb},${alpha.toFixed(2)})` };
};

const GA4_STATUS = {
  ativo: { dot: 'bg-green-500', label: 'GA4 ativo' },
  simulado: { dot: 'bg-amber-400', label: 'GA4 modo simulado' },
  nao_configurado: { dot: 'bg-slate-400', label: 'GA4 não configurado' },
  desativado: { dot: 'bg-slate-400', label: 'GA4 desativado' },
};

export const Dashboard = () => {
  const { projects, setSelectedProject, privateMode, setPrivateMode, setPrivateOrder } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const periodo = searchParams.get('periodo') || 'hoje';
  const inicio = searchParams.get('inicio') || '';
  const fim = searchParams.get('fim') || '';
  const privadoParam = searchParams.get('privado');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [tvMode, setTvMode] = useState(false);
  const [projTotal, setProjTotal] = useState(undefined);
  const containerRef = useRef(null);

  const includesToday = PERIODS_WITH_TODAY.has(periodo) || (periodo === 'personalizado' && fim >= todayISO());

  const applyPeriod = useCallback((value, start, end) => {
    const next = { periodo: value };
    if (value === 'personalizado') {
      next.inicio = start;
      next.fim = end;
    }
    if (privadoParam !== null) next.privado = privadoParam;
    setSearchParams(next);
    setExpanded(null);
  }, [setSearchParams, privadoParam]);

  useEffect(() => {
    if (privadoParam === '1') setPrivateMode(true);
    else if (privadoParam === '0') setPrivateMode(false);
  }, [privadoParam, setPrivateMode]);

  const togglePrivate = () => {
    const on = !privateMode;
    setPrivateMode(on);
    if (privadoParam !== null) {
      const next = new URLSearchParams(searchParams);
      next.set('privado', on ? '1' : '0');
      setSearchParams(next);
    }
  };

  useEffect(() => {
    setPrivateOrder((data?.projects || []).map((p) => p.project_id));
  }, [data, setPrivateOrder]);

  useEffect(() => {
    axios.get(`${API_URL}/api/dashboard/projection-total`)
      .then((r) => setProjTotal(r.data))
      .catch(() => setProjTotal(null));
  }, []);

  const onPeriodChange = (value) => {
    if (value === 'personalizado') {
      if (inicio && fim) {
        applyPeriod('personalizado', inicio, fim);
      } else {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 6);
        applyPeriod('personalizado', fmtDate(start), fmtDate(end));
      }
    } else {
      applyPeriod(value);
    }
  };

  const onPickerChange = (val) => {
    if (val.mode === 'custom') {
      applyPeriod('personalizado', val.startDate, val.endDate);
    } else if (val.mode === 'preset') {
      if (val.preset === '7') applyPeriod('ultimos_7_dias');
      else if (val.preset === '30') applyPeriod('ultimos_30_dias');
      else {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - (parseInt(val.preset, 10) - 1));
        applyPeriod('personalizado', fmtDate(start), fmtDate(end));
      }
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ periodo });
      if (periodo === 'personalizado' && inicio && fim) {
        params.set('inicio', inicio);
        params.set('fim', fim);
        params.set('data_inicio', inicio);
        params.set('data_fim', fim);
      }
      const res = await axios.get(`${API_URL}/api/dashboard/sales-overview?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      console.error('Error loading sales overview:', e);
    } finally {
      setLoading(false);
    }
  }, [periodo, inicio, fim]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!includesToday) return undefined;
    const id = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchData, includesToday]);

  const toggleTv = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
        setTvMode(true);
      } else {
        await document.exitFullscreen();
        setTvMode(false);
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    const onFs = () => setTvMode(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const openProject = (projectId) => {
    const p = projects.find((x) => x.id === projectId);
    if (p) setSelectedProject(p);
    navigate('/project-overview');
  };

  const exportData = async (format) => {
    try {
      const params = new URLSearchParams({ periodo, formato: format });
      if (periodo === 'personalizado' && inicio && fim) {
        params.set('data_inicio', inicio);
        params.set('data_fim', fim);
      }
      const res = await axios.get(
        `${API_URL}/api/dashboard/sales-overview/export?${params.toString()}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendas_${periodo}.${format === 'pdf' ? 'pdf' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting sales overview:', e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const projectsData = data?.projects || [];
  const totals = data?.totals || { period: {}, comparison1: {}, comparison2: null, variation1: {}, variation2: null };
  const period = data?.period || { label: '', card_title: 'Vendido', range_label: '' };
  const c1 = data?.comparison1 || { label: 'Período anterior' };
  const c2 = data?.comparison2 || null;
  const hasC2 = !!c2;
  const parciais = data?.dados_parciais;

  const periodControls = (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={periodo} onValueChange={onPeriodChange}>
        <SelectTrigger className="w-[180px]" data-testid="period-selector">
          <SelectValue placeholder="Período" />
        </SelectTrigger>
        <SelectContent>
          {PERIOD_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`period-option-${o.value}`}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {periodo === 'personalizado' && (
        <div data-testid="dashboard-date-range">
          <DateRangePicker
            mode="custom"
            startDate={inicio}
            endDate={fim}
            onChange={onPickerChange}
          />
        </div>
      )}
      <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
        <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
      </Button>
      <Button variant="outline" size="sm" onClick={() => exportData('csv')} data-testid="export-csv-btn">
        <Download className="h-4 w-4 mr-2" /> CSV
      </Button>
      <Button variant="outline" size="sm" onClick={() => exportData('pdf')} data-testid="export-pdf-btn">
        <FileText className="h-4 w-4 mr-2" /> PDF
      </Button>
      <Button variant="outline" size="sm" onClick={toggleTv} data-testid="tv-mode-btn">
        <Maximize2 className="h-4 w-4 mr-2" /> Modo TV
      </Button>
      <TooltipProvider>
        <UITooltip>
          <TooltipTrigger asChild>
            <Button variant={privateMode ? 'default' : 'outline'} size="sm" onClick={togglePrivate}
              aria-label={privateMode ? 'Mostrar nomes e valores' : 'Ocultar nomes e valores dos clientes'}
              aria-pressed={privateMode} data-testid="private-mode-btn">
              {privateMode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent data-testid="private-mode-tooltip">
            <p>{privateMode ? 'Mostrar nomes e valores' : 'Ocultar nomes e valores dos clientes'}</p>
          </TooltipContent>
        </UITooltip>
      </TooltipProvider>
    </div>
  );

  if (projectsData.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold tracking-tight">Dashboard de Vendas</h1>
              {privateMode && <PrivateBadge />}
            </div>
            <p className="text-sm text-muted-foreground mt-1">Visão geral de todos os projetos com Wake Commerce</p>
          </div>
          {periodControls}
        </div>
        <div className="flex flex-col items-center justify-center h-96 text-center" data-testid="dashboard-empty">
          <Plug size={64} className="text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Nenhum projeto com Wake ativa</h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            Ative a integração Wake Commerce em um projeto para acompanhar as vendas em tempo real aqui.
          </p>
          <Button onClick={() => navigate('/integrations')} data-testid="go-to-integrations-btn">
            Ir para Integrações
          </Button>
        </div>
      </div>
    );
  }

  const labels = buildPrivateLabels(projectsData.map((p) => p.project_id), projects);
  const nameOf = (id, name) => (privateMode ? labels[id] : name);
  const money = privateMode ? () => MASKED_BRL : brl;

  const healthFor = (proj) => {
    if (!proj) return { color: 'bg-slate-400', label: 'Wake não configurada' };
    const st = proj?.integration?.sync_status;
    const last = proj?.integration?.last_sync_at;
    const done = proj?.integration?.backfill_completed;
    const prog = proj?.integration?.backfill_progress;
    if (st === 'erro') return { color: 'bg-red-500', label: 'Erro de sincronização' };
    if (!done) {
      const eta = etaLabel(proj?.integration?.backfill_started_at, prog);
      return { color: 'bg-amber-400', label: `Importando histórico: ${prog ?? 0}%${eta ? ' · ' + eta : ''}` };
    }
    const mins = last ? (Date.now() - new Date(last).getTime()) / 60000 : Infinity;
    if (mins <= 30) return { color: 'bg-green-500', label: 'Sincronizada' };
    return { color: 'bg-amber-400', label: 'Sincronização atrasada' };
  };

  return (
    <TooltipProvider>
      <div ref={containerRef} className={`space-y-6 ${tvMode ? 'bg-background p-8 overflow-auto h-screen text-[1.15rem]' : ''}`} data-testid="dashboard">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold tracking-tight">Dashboard de Vendas</h1>
              {privateMode && <PrivateBadge />}
            </div>
            <p className="text-sm text-muted-foreground mt-1">Visão geral de todos os projetos com Wake Commerce</p>
          </div>
          {periodControls}
        </div>

        <KpiCards totals={totals} period={period} c1={c1} c2={c2} updatedAt={data?.updated_at} projTotal={projTotal} tvMode={tvMode} />

        {/* Partial data warning */}
        {parciais?.projetos?.length > 0 && (
          <div
            className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400"
            data-testid="partial-data-warning"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              {privateMode
                ? `Histórico ainda em importação para ${parciais.projetos.length} ${parciais.projetos.length === 1 ? 'projeto' : 'projetos'}. Os totais podem estar parciais.`
                : `Histórico ainda em importação para: ${parciais.projetos.join(', ')}`}
            </span>
          </div>
        )}

        {/* Ranking */}
        <Card data-testid="ranking-card">
          <CardHeader>
            <CardTitle>Ranking de projetos (por {period.label.toLowerCase()})</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-left">
                  <th className="py-2 px-3">Projeto</th>
                  <th className="py-2 px-3 text-right">{period.label}</th>
                  <th className="py-2 px-3 text-right">{(periodo === 'hoje' || periodo === 'ontem') ? c1.label : 'Período anterior'}</th>
                  <th className="py-2 px-3 text-right">Δ%</th>
                  {hasC2 && <th className="py-2 px-3 text-right">Semana passada</th>}
                  {hasC2 && <th className="py-2 px-3 text-right">Δ%</th>}
                  <th className="py-2 px-3 text-center">Status</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {projectsData.map((p) => {
                  const h = healthFor(p);
                  const dPrev = p.variation1?.bruto;
                  const dWeek = p.variation2?.bruto;
                  const isOpen = expanded === p.project_id;
                  return (
                    <React.Fragment key={p.project_id}>
                      <tr
                        className="border-b hover:bg-muted/50 cursor-pointer"
                        onClick={() => setExpanded(isOpen ? null : p.project_id)}
                        data-testid={`ranking-row-${p.project_id}`}
                      >
                        <td className="py-3 px-3 font-medium" data-testid={`ranking-name-${p.project_id}`}>{nameOf(p.project_id, p.project_name)}</td>
                        <td className="py-3 px-3 text-right font-mono">{money(p.period.bruto)}</td>
                        <td className="py-3 px-3 text-right font-mono text-muted-foreground">{money(p.comparison1.bruto)}</td>
                        <td className="py-3 px-3 text-right" style={deltaCellStyle(dPrev)}><Delta pct={dPrev} /></td>
                        {hasC2 && <td className="py-3 px-3 text-right font-mono text-muted-foreground">{money(p.comparison2?.bruto)}</td>}
                        {hasC2 && <td className="py-3 px-3 text-right" style={deltaCellStyle(dWeek)}><Delta pct={dWeek} /></td>}
                        <td className="py-3 px-3 text-center">
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <span className={`inline-block h-3 w-3 rounded-full ${h.color}`} />
                            </TooltipTrigger>
                            <TooltipContent><p>{h.label}</p></TooltipContent>
                          </UITooltip>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <Button variant="outline" size="sm" data-testid={`open-project-${p.project_id}`}
                            onClick={(e) => { e.stopPropagation(); openProject(p.project_id); }}>
                            Abrir projeto
                          </Button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`expanded-${p.project_id}`}>
                          <td colSpan={hasC2 ? 8 : 6} className="bg-muted/30 p-4">
                            <div className="grid gap-4 lg:grid-cols-4 mb-4">
                              <Indicator label="Pedidos" value={num(p.period.pedidos)} />
                              <Indicator label="Ticket médio" value={money(p.period.ticket_medio)} />
                              <Indicator label="Taxa de pagamento" value={`${(p.period.bruto ? (p.period.pago / p.period.bruto) * 100 : 0).toFixed(1).replace('.', ',')}%`} />
                              <Indicator label="Pago" value={money(p.period.pago)} />
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                              <LineChart data={p.series}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="data" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                                <YAxis stroke="hsl(var(--muted-foreground))" tick={privateMode ? false : { fontSize: 11 }} width={privateMode ? 10 : 60} />
                                <Tooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--popover-foreground))' }}
                                  formatter={privateMode ? (v, name) => ['', name] : (v) => brl(v)} separator={privateMode ? '' : ' : '} />
                                <Legend />
                                <Line type="monotone" dataKey="bruto" name="Bruto" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
                                <Line type="monotone" dataKey="pago" name="Pago" stroke="hsl(var(--chart-4))" strokeWidth={2} dot={false} />
                              </LineChart>
                            </ResponsiveContainer>
                            <div className="mt-4">
                              <ExpandedProjection projectId={p.project_id} privado={privateMode} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Integration health */}
        <Card data-testid="health-card">
          <CardHeader>
            <CardTitle>Saúde das integrações</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.ga4 && !data.ga4.global_active && (
              <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-sm" data-testid="ga4-global-warning">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Integração global com o GA4 está desativada. Ative em Configurações do sistema &gt; Integrações.
              </div>
            )}
            <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {projects.map((proj) => {
                const sd = projectsData.find((x) => x.project_id === proj.id);
                const h = healthFor(sd);
                const ga4Key = data?.ga4?.projects?.[proj.id] || 'nao_configurado';
                const ga4 = GA4_STATUS[ga4Key] || GA4_STATUS.nao_configurado;
                return (
                  <UITooltip key={proj.id}>
                    <TooltipTrigger asChild>
                      <div className="p-4 rounded-lg border bg-card flex flex-col gap-2" data-testid={`health-${proj.id}`}>
                        <div className="flex items-center gap-2">
                          <span className={`inline-block h-3 w-3 rounded-full ${h.color}`} />
                          <span className="font-medium truncate" data-testid={`health-name-${proj.id}`}>{nameOf(proj.id, proj.name)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`ga4-status-${proj.id}`}>
                          <span className={`inline-block h-2 w-2 rounded-full ${ga4.dot}`} />
                          {ga4.label}
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{h.label}</p>
                      {sd?.integration?.last_sync_at && <p className="font-mono text-xs">Último sync: {new Date(sd.integration.last_sync_at).toLocaleString('pt-BR')}</p>}
                    </TooltipContent>
                  </UITooltip>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

const Indicator = ({ label, value }) => (
  <div className="rounded-lg border bg-card p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="font-mono font-bold text-lg">{value}</p>
  </div>
);

const PrivateBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground" data-testid="private-mode-badge">
    <EyeOff className="h-3 w-3" /> Modo privado
  </span>
);

const ExpandedProjection = ({ projectId, privado }) => {
  const [data, setData] = useState(undefined);
  useEffect(() => {
    setData(undefined);
    axios.get(`${API_URL}/api/dashboard/projects/${projectId}/projection`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [projectId]);
  return (
    <div data-testid={`expanded-projection-${projectId}`}>
      <SalesProjectionView data={data} privado={privado} subtitle="Projeção do mês atual, independente do período selecionado" />
    </div>
  );
};
